import Navigation from "../components/Navigation";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

const AI = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top immediately when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    if (location.pathname === "/") {
      // On home page, scroll to contact section
      const element = document.querySelector("#contact");
      if (element) {
        const navHeight = 80;
        const elementPosition = element.offsetTop - navHeight;
        window.scrollTo({
          top: elementPosition,
          behavior: "smooth",
        });
        window.history.pushState(null, null, "#contact");
      }
    } else {
      // On other pages, navigate to home page with contact section
      navigate("/#contact");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContactClick();
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Skip link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Skip to main content
      </a>

      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {/* First Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy1.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy1.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy1.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy1.heroResults.results.conversionRate.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy1.heroResults.results.conversionRate.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy1.heroResults.results.availability.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy1.heroResults.results.availability.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy1.heroResults.results.savings.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("ai.caseStudy1.heroResults.results.savings.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy1.heroResults.results.hours.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("ai.caseStudy1.heroResults.results.hours.description")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy1.heroResults.results.csat.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("ai.caseStudy1.heroResults.results.csat.description")}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("ai.caseStudy1.heroResults.results.csat.detail")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy1.heroResults.results.timeToHire.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy1.heroResults.results.timeToHire.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("ai.caseStudy1.heroResults.results.timeToHire.detail")}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy1.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("ai.caseStudy1.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy1.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy1.complication.issues.knowledge.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.complication.issues.knowledge.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy1.complication.issues.backlog.title")}
                  </div>
                  <p className="text-gray-700">
                    {t("ai.caseStudy1.complication.issues.backlog.description")}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy1.complication.issues.onboarding.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.complication.issues.onboarding.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy1.complication.issues.gdpr.title")}
                  </div>
                  <p className="text-gray-700">
                    {t("ai.caseStudy1.complication.issues.gdpr.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy1.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("ai.caseStudy1.catalyst.description")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy1.catalyst.features.rag.title")}
                      </span>{" "}
                      {t("ai.caseStudy1.catalyst.features.rag.description")}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy1.catalyst.features.intent.title")}
                      </span>{" "}
                      {t("ai.caseStudy1.catalyst.features.intent.description")}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy1.catalyst.features.humanLoop.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy1.catalyst.features.humanLoop.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy1.catalyst.features.integration.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy1.catalyst.features.integration.description"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy1.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("ai.caseStudy1.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy1.impact.table.headers.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy1.impact.table.headers.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy1.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy1.impact.table.rows.conversionRate.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.conversionRate.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.conversionRate.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy1.impact.table.rows.conversionRate.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.responseTime.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.responseTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.responseTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy1.impact.table.rows.responseTime.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.backlog.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.backlog.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.backlog.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.backlog.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.onboarding.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.onboarding.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.onboarding.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.onboarding.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.hours.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.hours.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.hours.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.hours.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.csat.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.csat.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.csat.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.csat.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy1.impact.table.rows.employeeSatisfaction.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.employeeSatisfaction.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy1.impact.table.rows.employeeSatisfaction.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy1.impact.table.rows.employeeSatisfaction.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.timeToHire.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.timeToHire.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.timeToHire.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.timeToHire.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy1.impact.table.rows.opex.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.opex.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy1.impact.table.rows.opex.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy1.impact.table.rows.opex.delta")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy1.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy1.successFactors.factors.combination.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.successFactors.factors.combination.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy1.successFactors.factors.availability.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.successFactors.factors.availability.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("ai.caseStudy1.successFactors.factors.automation.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.successFactors.factors.automation.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("ai.caseStudy1.successFactors.factors.techSetup.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy1.successFactors.factors.techSetup.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("ai.caseStudy1.successFactors.factors.gdpr.title")}
                  </div>
                  <p className="text-gray-700">
                    {t("ai.caseStudy1.successFactors.factors.gdpr.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("ai.caseStudy1.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("ai.caseStudy1.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("ai.caseStudy1.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("ai.caseStudy1.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("ai.caseStudy1.callToAction.button")}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-4 border-gray-200 my-16"></div>

          {/* Second Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy2.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy2.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy2.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy2.heroResults.results.onboardingTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.onboardingTime.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(
                      "ai.caseStudy2.heroResults.results.onboardingTime.detail"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy2.heroResults.results.firstQuarterRevenue.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.firstQuarterRevenue.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy2.heroResults.results.coachingCosts.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.coachingCosts.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy2.heroResults.results.grossPremiums.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.grossPremiums.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy2.heroResults.results.partnerCsat.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.partnerCsat.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("ai.caseStudy2.heroResults.results.partnerCsat.detail")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy2.heroResults.results.earlyAttrition.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy2.heroResults.results.earlyAttrition.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy2.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("ai.caseStudy2.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy2.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy2.complication.issues.rampUpCosts.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.complication.issues.rampUpCosts.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "ai.caseStudy2.complication.issues.inefficientAllocation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.complication.issues.inefficientAllocation.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "ai.caseStudy2.complication.issues.missingForecast.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.complication.issues.missingForecast.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "ai.caseStudy2.complication.issues.lowTransparency.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.complication.issues.lowTransparency.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy2.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("ai.caseStudy2.catalyst.description")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy2.catalyst.features.xgboostPredictor.title"
                        )}
                      </span>
                      ,{" "}
                      {t(
                        "ai.caseStudy2.catalyst.features.xgboostPredictor.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy2.catalyst.features.shapLayer.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy2.catalyst.features.shapLayer.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy2.catalyst.features.apiIntegration.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy2.catalyst.features.apiIntegration.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy2.catalyst.features.automatedWorkflows.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy2.catalyst.features.automatedWorkflows.description"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy2.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("ai.caseStudy2.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy2.impact.table.headers.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy2.impact.table.headers.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy2.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy2.impact.table.rows.onboardingTime.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.onboardingTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.onboardingTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy2.impact.table.rows.onboardingTime.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy2.impact.table.rows.firstQuarterRevenue.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.firstQuarterRevenue.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.firstQuarterRevenue.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy2.impact.table.rows.firstQuarterRevenue.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy2.impact.table.rows.coachingCosts.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.coachingCosts.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.coachingCosts.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy2.impact.table.rows.coachingCosts.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy2.impact.table.rows.grossPremiums.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.grossPremiums.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.grossPremiums.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy2.impact.table.rows.grossPremiums.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy2.impact.table.rows.partnerCsat.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.partnerCsat.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy2.impact.table.rows.partnerCsat.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy2.impact.table.rows.partnerCsat.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy2.impact.table.rows.earlyAttrition.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.earlyAttrition.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy2.impact.table.rows.earlyAttrition.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy2.impact.table.rows.earlyAttrition.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy2.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy2.successFactors.factors.predictiveScoring.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.successFactors.factors.predictiveScoring.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy2.successFactors.factors.shapExplainability.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.successFactors.factors.shapExplainability.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy2.successFactors.factors.directLeadRouting.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.successFactors.factors.directLeadRouting.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy2.successFactors.factors.gamifiedDashboards.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.successFactors.factors.gamifiedDashboards.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy2.successFactors.factors.onPremiseHosting.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy2.successFactors.factors.onPremiseHosting.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("ai.caseStudy2.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("ai.caseStudy2.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("ai.caseStudy2.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("ai.caseStudy2.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("ai.caseStudy2.callToAction.button")}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-4 border-gray-200 my-16"></div>

          {/* Third Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy3.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy3.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy3.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy3.heroResults.results.turnaroundTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy3.heroResults.results.turnaroundTime.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(
                      "ai.caseStudy3.heroResults.results.turnaroundTime.detail"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy3.heroResults.results.errorRate.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy3.heroResults.results.errorRate.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy3.heroResults.results.fteCapacity.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy3.heroResults.results.fteCapacity.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy3.heroResults.results.partnerCsat.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy3.heroResults.results.partnerCsat.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t("ai.caseStudy3.heroResults.results.partnerCsat.detail")}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy3.heroResults.results.roi.value")}
                  </div>
                  <div className="text-gray-700">
                    {t("ai.caseStudy3.heroResults.results.roi.description")}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy3.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("ai.caseStudy3.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy3.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "ai.caseStudy3.complication.issues.formValidation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.complication.issues.formValidation.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t(
                      "ai.caseStudy3.complication.issues.manualContracts.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.complication.issues.manualContracts.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy3.complication.issues.mediaBreaks.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.complication.issues.mediaBreaks.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy3.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("ai.caseStudy3.catalyst.description")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy3.catalyst.features.adaptiveWebform.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy3.catalyst.features.adaptiveWebform.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy3.catalyst.features.apiBridge.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy3.catalyst.features.apiBridge.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy3.catalyst.features.documentOcr.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy3.catalyst.features.documentOcr.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy3.catalyst.features.realTimeFeedback.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy3.catalyst.features.realTimeFeedback.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t("ai.caseStudy3.catalyst.features.bpmEngine.title")}
                      </span>{" "}
                      {t(
                        "ai.caseStudy3.catalyst.features.bpmEngine.description"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy3.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("ai.caseStudy3.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy3.impact.table.headers.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy3.impact.table.headers.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy3.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy3.impact.table.rows.turnaroundTime.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy3.impact.table.rows.turnaroundTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy3.impact.table.rows.turnaroundTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy3.impact.table.rows.turnaroundTime.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy3.impact.table.rows.errorRate.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.errorRate.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.errorRate.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy3.impact.table.rows.errorRate.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy3.impact.table.rows.fteCapacity.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy3.impact.table.rows.fteCapacity.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.fteCapacity.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy3.impact.table.rows.fteCapacity.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy3.impact.table.rows.partnerCsat.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy3.impact.table.rows.partnerCsat.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.partnerCsat.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy3.impact.table.rows.partnerCsat.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy3.impact.table.rows.backlog.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.backlog.before")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy3.impact.table.rows.backlog.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy3.impact.table.rows.backlog.delta")}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy3.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy3.successFactors.factors.liveValidation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.successFactors.factors.liveValidation.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy3.successFactors.factors.autoContractCreation.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.successFactors.factors.autoContractCreation.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("ai.caseStudy3.successFactors.factors.apiFirst.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.successFactors.factors.apiFirst.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy3.successFactors.factors.transparentStatus.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.successFactors.factors.transparentStatus.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl md:col-span-2">
                  <div className="font-semibold text-green-800 mb-2">
                    {t("ai.caseStudy3.successFactors.factors.noCodeBpm.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy3.successFactors.factors.noCodeBpm.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("ai.caseStudy3.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("ai.caseStudy3.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("ai.caseStudy3.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("ai.caseStudy3.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                {t("ai.caseStudy3.callToAction.button")}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t-4 border-gray-200 my-16"></div>

          {/* Fourth Case Study */}
          <div className="mb-20">
            {/* Header */}
            <div className="mb-16">
              <h1 className="text-4xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy4.title")}
              </h1>
              <h2 className="text-2xl font-semibold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy4.subtitle")}
              </h2>
            </div>

            {/* Hero Results */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy4.heroResults.title")}
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy4.heroResults.results.savedHours.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.savedHours.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy4.heroResults.results.reportingTime.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.reportingTime.description"
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {t(
                      "ai.caseStudy4.heroResults.results.reportingTime.detail"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy4.heroResults.results.selfServiceRate.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.selfServiceRate.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t("ai.caseStudy4.heroResults.results.dataSources.value")}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.dataSources.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy4.heroResults.results.lastMinuteFlights.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.lastMinuteFlights.description"
                    )}
                  </div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {t(
                      "ai.caseStudy4.heroResults.results.developmentTime.value"
                    )}
                  </div>
                  <div className="text-gray-700">
                    {t(
                      "ai.caseStudy4.heroResults.results.developmentTime.description"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Circumstance */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy4.circumstance.title")}
              </h3>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {t("ai.caseStudy4.circumstance.description")}
                </p>
              </div>
            </div>

            {/* Complication */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy4.complication.title")}
              </h3>
              <div className="space-y-4">
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy4.complication.issues.timeDelay.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.complication.issues.timeDelay.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy4.complication.issues.dataBreaks.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.complication.issues.dataBreaks.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy4.complication.issues.peakLoad.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.complication.issues.peakLoad.description"
                    )}
                  </p>
                </div>
                <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-red-800 mb-2">
                    {t("ai.caseStudy4.complication.issues.blindSpots.title")}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.complication.issues.blindSpots.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Catalyst */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy4.catalyst.title")}
              </h3>
              <div className="bg-blue-50 rounded-xl p-6">
                <p className="text-lg text-gray-700 mb-6">
                  {t("ai.caseStudy4.catalyst.description")}
                </p>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.powerQueryDataflows.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.powerQueryDataflows.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.azureSqlDataLake.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.azureSqlDataLake.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.powerBiDataset.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.powerBiDataset.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.powerBiService.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.powerBiService.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.powerAutomate.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.powerAutomate.description"
                      )}
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <span className="font-semibold">
                        {t(
                          "ai.caseStudy4.catalyst.features.zeroCodeApproach.title"
                        )}
                      </span>{" "}
                      {t(
                        "ai.caseStudy4.catalyst.features.zeroCodeApproach.description"
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Impact */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-6">
                {t("ai.caseStudy4.impact.title")}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-lg">
                  <thead className="bg-[#0F1B2D] text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-semibold">
                        {t("ai.caseStudy4.impact.table.headers.kpi")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy4.impact.table.headers.before")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy4.impact.table.headers.after")}
                      </th>
                      <th className="px-6 py-4 text-center font-semibold">
                        {t("ai.caseStudy4.impact.table.headers.delta")}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy4.impact.table.rows.reportingTime.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.reportingTime.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.reportingTime.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy4.impact.table.rows.reportingTime.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t("ai.caseStudy4.impact.table.rows.manualHours.kpi")}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.manualHours.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t("ai.caseStudy4.impact.table.rows.manualHours.after")}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t("ai.caseStudy4.impact.table.rows.manualHours.delta")}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy4.impact.table.rows.selfServiceQueries.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.selfServiceQueries.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.selfServiceQueries.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy4.impact.table.rows.selfServiceQueries.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy4.impact.table.rows.lastMinuteFlights.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.lastMinuteFlights.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.lastMinuteFlights.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy4.impact.table.rows.lastMinuteFlights.delta"
                        )}
                      </td>
                    </tr>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium">
                        {t(
                          "ai.caseStudy4.impact.table.rows.economyVsBusiness.kpi"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.economyVsBusiness.before"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {t(
                          "ai.caseStudy4.impact.table.rows.economyVsBusiness.after"
                        )}
                      </td>
                      <td className="px-6 py-4 text-center text-green-600 font-semibold">
                        {t(
                          "ai.caseStudy4.impact.table.rows.economyVsBusiness.delta"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Success Factors */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold text-[#0F1B2D] mb-4">
                {t("ai.caseStudy4.successFactors.title")}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy4.successFactors.factors.lowCodeFirst.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.successFactors.factors.lowCodeFirst.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy4.successFactors.factors.unifiedDataModel.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.successFactors.factors.unifiedDataModel.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy4.successFactors.factors.liveInsights.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.successFactors.factors.liveInsights.description"
                    )}
                  </p>
                </div>
                <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-xl">
                  <div className="font-semibold text-green-800 mb-2">
                    {t(
                      "ai.caseStudy4.successFactors.factors.governanceByDesign.title"
                    )}
                  </div>
                  <p className="text-gray-700">
                    {t(
                      "ai.caseStudy4.successFactors.factors.governanceByDesign.description"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Customer Quote */}
            <div className="mb-12">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-8">
                <div className="flex items-start space-x-4">
                  <div className="text-4xl text-indigo-600">"</div>
                  <div>
                    <p className="text-lg text-gray-700 italic mb-4">
                      {t("ai.caseStudy4.customerQuote.quote")}
                    </p>
                    <div className="font-semibold text-[#0F1B2D]">
                      {t("ai.caseStudy4.customerQuote.author")}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-[#C5A66A]">
                {t("ai.caseStudy4.callToAction.title")}
              </h3>
              <p className="text-lg mb-6">
                {t("ai.caseStudy4.callToAction.description")}
              </p>
              <button
                onClick={handleContactClick}
                onKeyDown={handleKeyDown}
                className="bg-[#C5A66A] cursor-pointer text-[#0F1B2D] px-8 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
                aria-label={t("ai.caseStudy4.callToAction.button")}
              >
                {t("ai.caseStudy4.callToAction.button")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AI;
